'use client';

import { cn } from 'noddi-ui-common';
import { Slider as SliderPrimitive } from 'radix-ui';
import * as React from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../base/tooltip';

interface SliderProps extends React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root> {
  showTooltip?: boolean;
  className?: string;
  defaultValue?: number[];
  value?: number[];
  onValueChange?: (value: number[]) => void;
  min?: number;
  max?: number;
  step?: number;
  formatLabel?: (value: number) => string;
  showMarks?: boolean;
  customMarks?: { value: number; label: string }[];
  showSelectedLabel?: boolean;
}

const NoddiSlider = React.forwardRef<HTMLElement, SliderProps>(
  (
    {
      className,
      showTooltip = false,
      // Radix puts role="slider" on the thumb, so a name left on Root would never reach it.
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      ...props
    },
    ref
  ) => {
    const [values, setValues] = React.useState(props.defaultValue || props.value || [0]);
    const [showTooltipState, setShowTooltipState] = React.useState(false);

    const handlePointerDown = () => {
      setShowTooltipState(true);
    };

    const handlePointerUp = () => {
      setShowTooltipState(false);
    };

    React.useEffect(() => {
      if (props.value) {
        setValues(props.value);
      }
    }, [props.value]);

    const handleValueChange = (newValues: number[]) => {
      setValues(newValues);
      props.onValueChange?.(newValues);
    };

    React.useEffect(() => {
      document.addEventListener('pointerup', handlePointerUp);
      return () => {
        document.removeEventListener('pointerup', handlePointerUp);
      };
    }, []);

    return (
      <SliderPrimitive.Root
        ref={ref}
        className={cn('relative flex w-full touch-none items-center select-none', className)}
        onValueChange={handleValueChange}
        onPointerDown={handlePointerDown}
        {...props}
      >
        <SliderPrimitive.Track className='relative h-2 w-full grow overflow-hidden rounded-full bg-secondary'>
          <SliderPrimitive.Range className='absolute h-full bg-primary' />
        </SliderPrimitive.Track>
        <TooltipProvider>
          <Tooltip open={showTooltip && showTooltipState}>
            <TooltipTrigger asChild>
              <SliderPrimitive.Thumb
                aria-label={ariaLabel}
                aria-labelledby={ariaLabelledBy}
                className='block size-5 rounded-full border-2 border-primary bg-background transition-colors focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-hidden disabled:pointer-events-none disabled:opacity-50'
                onMouseEnter={() => setShowTooltipState(true)}
                onMouseLeave={() => setShowTooltipState(false)}
              />
            </TooltipTrigger>
            <TooltipContent>
              <p>{values[0]}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </SliderPrimitive.Root>
    );
  }
);

NoddiSlider.displayName = SliderPrimitive.Root.displayName;

export { NoddiSlider };
